// packages block
import { useContext, useEffect, useRef, useState } from "react";
import Select from "react-select";
import { toast } from "react-toastify";
import { useMutation, useQuery } from "@apollo/client";
import { Controller, useForm } from "react-hook-form";
import { Badge, Button, Form, Label, Modal, ModalBody, ModalFooter } from "reactstrap";
// components block
import InputField from "../../../components/layout/InputField";
import Loader from "../../../components/layout/Loader";
// constant and graphql block
import { REFERENCE_FORM, SAVE_REFERENCE_FORM } from "./gql";
import { EMAIL_PATTERN, PHONE_PATTERN, REFERENCE_STATUS } from "../../../utils/constant";
import {
  buildDefaultValues,
  hasRelationshipQuestion,
  ReferenceFormQuestion,
  ReferenceFormValues,
  splitQuestions,
  toSaveInput,
} from "./referenceFormUtils";

import CLOSE_ICON from "../../../assets/images/close-icon.svg";
import { AppContext } from "../../../context";
import { customSelectDropDown } from "../../../components/styled/customSelect";

// kan-118 — the Reference Form renders the candidate's CURRENT AI Reference
// Taker question set (merged server-side with this reference's stored answers)
// instead of nine hard-coded textareas, and saves through saveReferenceForm so
// the preview/PDF (which read AiReferenceAnswers rows) see recruiter edits.
// Adding/removing/reordering questions stays in the gear modal; this form only
// edits answers.
const AddReferenceModal = ({
  isOpen,
  toggle,
  refetch,
  candidateId,
  referenceId,
  modalHeader,
  referenceData,
}) => {
  const { theme } = useContext(AppContext)
  const { formState: { errors }, handleSubmit, register, reset, control, getValues } =
    useForm<ReferenceFormValues>();
  const [saving, setSaving] = useState(false);
  const [saveReferenceForm] = useMutation(SAVE_REFERENCE_FORM);

  const editingId: string | null = referenceData?.id ?? null;
  const { data, loading, error } = useQuery(REFERENCE_FORM, {
    variables: { candidateId, referenceId: editingId },
    skip: !isOpen || !candidateId,
    fetchPolicy: "network-only",
  });
  const form = data?.referenceForm;
  // Ignore a stale result left over from a previous open for another reference.
  const formMatches =
    !!form && (form.referenceId ?? null) === editingId && form.candidateId === candidateId;
  const questions: ReferenceFormQuestion[] = formMatches ? form.questions ?? [] : [];
  const { current, orphans } = splitQuestions(questions);
  const showRelationshipInput = !hasRelationshipQuestion(questions);
  const ready = formMatches && !loading && !error;

  // Hydrate once per open, after the query resolves, so in-progress typing is
  // never clobbered by a cache update and a cancelled edit doesn't leak into
  // the next open.
  // Contact fields hydrate immediately on open (they don't depend on the
  // query); answers merge in once it resolves, keeping any contact typing.
  const hydrated = useRef(false);
  useEffect(() => {
    if (!isOpen) {
      hydrated.current = false;
      return;
    }
    reset(buildDefaultValues(referenceData, []));
    // eslint-disable-next-line
  }, [isOpen, referenceData]);
  useEffect(() => {
    if (isOpen && ready && !hydrated.current) {
      reset({ ...getValues(), answers: buildDefaultValues(referenceData, questions).answers });
      hydrated.current = true;
    }
    // eslint-disable-next-line
  }, [isOpen, ready, form]);

  const addOrUpdateReference = async (values: ReferenceFormValues) => {
    if (!ready) return;
    const isCreate = !editingId;
    setSaving(true);
    try {
      const res = await saveReferenceForm({
        variables: {
          input: toSaveInput(values, questions, { candidateId, referenceId: editingId }),
        },
      });
      if (res?.data?.saveReferenceForm) {
        toast.success(
          isCreate ? "Reference Successfully Created!" : "Reference Successfully Updated!",
        );
        refetch();
        toggle();
      }
    } catch (err: any) {
      // Surface the API's message (e.g. the in-flight 409 "can't be edited
      // while the AI Reference Taker is working on it").
      toast.error(err?.graphQLErrors?.[0]?.message || err?.message || "Unable to save reference.");
    } finally {
      setSaving(false);
    }
  };

  const renderQuestion = ({ question, index }: { question: ReferenceFormQuestion; index: number }) => (
    <div className="mb-2" key={`${question.templateId ?? "m"}-${index}`}>
      <Label title={question.prompt || undefined}>{question.label}</Label>
      <textarea
        className="form-control"
        rows={4}
        id={`referenceAnswer${index}`}
        placeholder="Notes ..."
        {...register(`answers.${index}` as const, { required: false })}
      ></textarea>
    </div>
  );

  const customStyles = {
    control: (styles, provided) => ({
      ...styles,

      fontSize: 12,
    }),

    option: (provided, state) => ({
      ...provided,
      fontSize: 12,
    }),

    container: (provided, state) => ({
      ...provided,
      fontSize: 12,
    }),
  };

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      className="revampDialog revampDialogWidth add-reference-dialog"
    >
      <div className="modalHeaderCustom d-flex align-items-center justify-content-between">
        <div className="d-flex align-items-center">
          <h5 className="m-0">{modalHeader}</h5>

          <div className="ml-2">
            {referenceData?.status && (
              <Badge
                color={
                  referenceData?.status === "COMPLETED"
                    ? "success"
                    : referenceData?.status === "PENDING"
                      ? "warning"
                      : ""
                }
                className="text-capitalize"
              >
                {referenceData?.status ?? "--"}
              </Badge>
            )}
          </div>
        </div>

        <Button
          className="p-0 bg-transparent border-0"
          onClick={() => toggle()}
        >
          <img src={CLOSE_ICON} alt="close" />
        </Button>
      </div>
      <Form onSubmit={handleSubmit(addOrUpdateReference)}>
        <ModalBody className="modalBodyScroll candidateInterview">
          <InputField
            label="Reference Name"
            inputtype="text"
            isRequired={true}
            inputid="referenceName"
            placeholder={"Jessica Hasson"}
            {...register('referenceName', {
              required: {
                value: true,
                message: "Reference Name is required!",
              },
            })}
            errMsg={errors?.referenceName?.message as any}
          />

          <InputField
            label="Title"
            inputtype="text"
            inputid="referenceTitle"
            placeholder={"Operations Manager"}
            {...register('title', { required: false })}
          />

          <InputField
            label="Company"
            inputtype="text"
            inputid="referenceCompany"
            placeholder={"Realty Income Corporation"}
            {...register('company', { required: false })}
          />

          <Label>
            Status <span className="text-danger">*</span>
          </Label>
          <Controller
            name="status"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Select
                className="mb-2"
                options={REFERENCE_STATUS}
                onChange={onChange}
                value={value}
                placeholder="Status"
                styles={{ ...customSelectDropDown(theme) }}
              />
            )}
          />

          <InputField
            inputtype="text"
            isRequired={false}
            label="Phone Number"
            inputid="phoneNumber"
            placeholder={"(207)555-0119"}
            {...register('phoneNumber', {
              required: {
                value: false,
                message: "Phone number not valid!",
              },
              pattern: {
                value: PHONE_PATTERN,
                message: "Phone Number is not valid!",
              },
            })}
            errMsg={errors?.phoneNumber?.message as any}
          />

          {/* Only once the question set is loaded: while loading, questions is
              [] and the input would flash in and then vanish, dropping any text. */}
          {ready && showRelationshipInput && (
            <InputField
              label="Relationship to Candidate"
              inputtype="text"
              inputid="relationshipToCandidate"
              placeholder={"Brother"}
              {...register('relationshipToCandidate', { required: false })}
            />
          )}

          <InputField
            inputtype="text"
            isRequired={false}
            label="Reference Email"
            inputid="email"
            placeholder="someone@example.com"
            {...register('email', {
              required: {
                value: false,
                message: "Email is not valid!",
              },
              pattern: {
                value: EMAIL_PATTERN,
                message: "Email is not valid!",
              },
            })}
            errMsg={errors?.email?.message as any}
          />

          <div className="mt-3">
            <h6 className="mb-1">Reference Questions</h6>
            <small className="text-muted d-block mb-2">
              Questions come from this candidate's AI Reference Taker question set (gear icon in the section above).
            </small>

            {loading && <Loader loaderClass="sm-height" />}

            {!loading && error && (
              <small className="text-danger d-block">
                Couldn't load this candidate's reference questions: {error.message}
              </small>
            )}

            {ready && current.map(renderQuestion)}

            {ready && orphans.length > 0 && (
              <>
                <hr />
                <h6 className="mb-1">No longer in this candidate's question set</h6>
                <small className="text-muted d-block mb-2">
                  Clear the text to drop it from the reference report.
                </small>
                {orphans.map(renderQuestion)}
              </>
            )}
          </div>
        </ModalBody>

        <ModalFooter className="pt-3">
          <div className="d-flex align-items-center">
            <button
              type="button"
              onClick={() => {
                toggle();
              }}
              className="buttonPrimary mr-3"
            >
              Cancel
            </button>

            <button
              type="submit"
              className="buttonGenericStyle filledButton"
              disabled={!ready || saving}
            >
              {referenceId ? "Update" : "Add"}
            </button>
          </div>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AddReferenceModal;
